import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import apiClient from '../../services/apiClient'
import StudentCaseStudyForm, {
  CaseStudyRecord,
} from '../../components/homework/StudentCaseStudyForm'

type HubItem = {
  id: number
  name: string
  courseId: number
  courseName: string
  courseCode: string
  deadline?: string | null
  submissionStatus?: string | null
  homeworkType?: string | null
  visibleCommentCount?: number
  adminFinalNote?: string | null
  completionNote?: string | null
}

function statusLabel(status?: string | null, homeworkType?: string | null) {
  if (homeworkType === 'self_practice') {
    if (status === 'completed') {
      return { text: 'Hoàn thành', className: 'bg-emerald-100 text-emerald-800' }
    }
    return { text: 'Chưa làm', className: 'bg-m2-surface-high text-m2-on-surface-variant' }
  }
  switch (status) {
    case 'draft':
      return { text: 'Nháp', className: 'bg-slate-100 text-slate-700' }
    case 'submitted':
      return { text: 'Đã nộp', className: 'bg-sky-100 text-sky-800' }
    case 'in_review':
      return { text: 'Đang chấm', className: 'bg-amber-100 text-amber-900' }
    case 'completed':
      return { text: 'Hoàn thành', className: 'bg-emerald-100 text-emerald-800' }
    case 'returned':
      return { text: 'Trả bài', className: 'bg-orange-100 text-orange-800' }
    default:
      return { text: 'Chưa làm', className: 'bg-m2-surface-high text-m2-on-surface-variant' }
  }
}

export default function StudentHomeworkHubPage() {
  const [items, setItems] = useState<HubItem[]>([])
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [caseEligible, setCaseEligible] = useState(false)
  const [caseCourse, setCaseCourse] = useState<{ courseId: number; courseName: string } | null>(
    null
  )
  const [caseStudy, setCaseStudy] = useState<CaseStudyRecord>(null)

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q.trim()), 300)
    return () => window.clearTimeout(t)
  }, [q])

  const load = async (search: string) => {
    try {
      setLoading(true)
      const res = await apiClient.get('/student/homeworks', {
        params: { q: search || undefined },
      })
      const data = res.data?.data
      setItems(data?.items || [])
      setCaseEligible(Boolean(data?.caseStudyEligible))
      setCaseCourse(data?.caseStudyCourse || null)
      setCaseStudy(data?.caseStudy || null)
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được BTVN')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load(debouncedQ)
  }, [debouncedQ])

  const needsCaseStudy = caseEligible && !caseStudy

  return (
    <div className="max-w-4xl mx-auto p-4 lg:p-8">
      <h1 className="text-2xl font-bold mb-1">BTVN của tôi</h1>
      <p className="text-sm text-m2-on-surface-variant mb-4">
        Danh sách bài tập về nhà theo lớp bạn đang học.
      </p>

      {caseEligible && (
        <div className="mb-6">
          <StudentCaseStudyForm
            courseName={caseCourse?.courseName}
            initial={caseStudy}
            onSaved={(record) => setCaseStudy(record)}
          />
        </div>
      )}

      {needsCaseStudy ? (
        <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          Sau khi đăng ký case study, danh sách BTVN sẽ hiện ở đây để bạn bắt đầu làm bài.
        </p>
      ) : (
        <>
          <div className="mb-4">
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Tìm BTVN / tên khóa / mã khóa..."
              className="w-full border border-m2-outline-variant rounded-md px-3 py-2 text-sm"
            />
          </div>

          {loading ? (
            <p className="text-m2-on-surface-variant">Đang tải...</p>
          ) : items.length === 0 ? (
            <p className="text-m2-on-surface-variant">
              Chưa có BTVN nào được gán cho lớp của bạn.
            </p>
          ) : (
            <div className="space-y-3">
              {items.map((hw) => {
                const st = statusLabel(hw.submissionStatus, hw.homeworkType)
                return (
                  <Link
                    key={hw.id}
                    to={`/student/courses/${hw.courseId}/homeworks/${hw.id}`}
                    className="block rounded-lg border border-m2-outline-variant px-4 py-3 hover:bg-m2-primary/5"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-m2-on-surface">
                          {hw.name}{' '}
                          {hw.homeworkType === 'self_practice' && (
                            <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
                              Tự thực hành
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-m2-on-surface-variant mt-1">
                          {hw.courseCode} — {hw.courseName}
                          {hw.visibleCommentCount
                            ? ` · ${hw.visibleCommentCount} comment`
                            : ''}
                          {hw.deadline
                            ? ` · Hạn: ${new Date(hw.deadline).toLocaleDateString('vi-VN')}`
                            : ''}
                        </div>
                      </div>
                      <span
                        className={`shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full ${st.className}`}
                      >
                        {st.text}
                      </span>
                    </div>
                    {hw.adminFinalNote && (
                      <div className="mt-2 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-950">
                        <span className="font-semibold">Nhận xét admin: </span>
                        {hw.adminFinalNote}
                      </div>
                    )}
                  </Link>
                )
              })}
            </div>
          )}
        </>
      )}
    </div>
  )
}
